/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './D3Chart';
import { helpRenders } from './D3Chart_Help';

const D3ChartManager: ComponentManagerFn<'D3Chart'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'D3Chart'>(platform, 'D3Chart'),
    type: 'D3Chart',
    name: 'D3 Chart',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      d3Script: {
        label: 'D3 Script',
        type: 'typescript',
        compileOnChange: true,
        defaultScript: `import { parseISO } from 'date-fns';
import * as d3 from 'd3';
import { D3Props, d3uid } from 'cloudio';

const TOOLTIP_ON_TOP = true;

// mount function is called when the D3 Chart item is mounted
// do your initailization here. This function is called only once
// when the D3 Chart item is mounted. if the user navigates away
// from the page and comes back, this function will be called again
function mount(props: D3Props) {
  console.log('mount', props);
}

// render function is called whenever the props passed to the D3 Chart item changes
// e.g. when the dataset or the width or height of the D3 Chart item changes
function render(props: D3Props) {
  console.log('render', props);
  const {
    dataset, // this is the dataset passed to the D3 Chart item from the parent data store reference defined above the D3 Chart item
    svg, // this is the root svg element of the D3 Chart item
    width, // this is the width of the D3 Chart item
    height, // this is the height of the D3 Chart item
    onClickTrigger, // this is the callback function to invoke any On Click Triggers defined inside the D3 Chart item
    tooltipRef, // the tooltip div element
    rootRef, // the root div element
    theme, // the material-ui theme object
  } = props;
}

// mount function is called when the D3 Chart item is unmounted
// use this to clean up any work done in the mount function
// this function will be called when the D3 Chart item is unmounted
// due to the user navigating away from the page or the D3 Chart item being removed from the dashboard
function unmount(props: D3Props) {
  console.log('unmount', props);
}

export default { render, mount, unmount };
`,
        helpText:
          'Refer https://github.com/cloudio-saas/d3 for build the chart locally',
      },
      noDataFoundMessage: {
        label: 'No Data Found Message',
        type: 'string',
      },
    },
    category: 'Dashboard',
    icon: { iconName: 'chart-line' },
    description: `**Overview**<br/>
The D3 Chart component is a fully scriptable visualization canvas that gives you absolute control over how your data is rendered. Unlike standard chart widgets (like Bar or Pie charts) that offer pre-defined behaviors, the D3 Chart provides a raw SVG environment powered by the D3.js library.
Use this component when you need to build custom, highly specialized visualizations—such as Sankey diagrams, Network graphs, custom heatmaps, or animated timeline simulations—that cannot be achieved with standard charting tools.

**1. The Mental Model**<br/>
Think of the D3 Chart not as a "Chart," but as a Managed Runtime Environment.
* **We provide:** The data stream, the SVG container, responsive dimensions, and the application theme.
* **You provide:** The rendering logic.
You write a TypeScript module that hooks into the component's lifecycle. The platform handles the plumbing (data fetching and resizing), allowing you to focus purely on the visual D3 logic.

**2. Lifecycle Management**<br/>
To ensure performance and prevent memory leaks, your script must export an object containing three specific lifecycle functions:
* **mount(props):**
    * **When it runs:** Once, when the component is first created.
    * **What to do:** Initialize static elements (e.g., axes groups, root <g> elements), define scales that don't change, or register global listeners.
* **render(props):**
    * **When it runs:** Every time the data changes, the screen resizes, or the parent re-renders.
    * **What to do:** This is your reactive loop. Use D3's "Enter-Update-Exit" pattern here to draw elements, update positions, and handle transitions based on the current props.
* **unmount(props):**
    * **When it runs:** When the component is removed from the DOM (e.g., navigation).
    * **What to do:** Clean up timers, remove manual event listeners, or cancel heavy calculations to free up memory.

**3. Environment & Data Injection**<br/>
Your script receives a props object containing everything needed to draw the chart.
* **dataset:** The array of raw data records passed down from the parent Data Store.
* **svg:** A reference to the root <svg> DOM element. Attach your D3 selectors to this.
* **width / height:** The current dimensions of the container. These update automatically on resize, allowing you to make your chart responsive.
* **theme:** The current application theme object (colors, typography), ensuring your custom chart matches the rest of the UI.
* **tooltipRef:** A reference to a pre-created HTML div element, ready to be used for custom tooltips.
* **onClickTrigger:** A callback function. Call this with a payload (e.g., a record ID) to trigger CloudIO workflows when a user clicks a visual element.

**4. Configuration**<br/>
* **D3 Script:** The editor where you write your TypeScript logic.
* **No Data Found Message:** Text displayed in the center of the container if the dataset is empty (e.g., "No records available").

**5. Integration & Interaction**<br/>
* **Data Source:** The D3 Chart does not select a data store directly. It must be placed inside a Data Store Reference or similar container that passes data down to it.
* **TriggerOnClick:** You can add a TriggerOnClick component as a child. Inside your script, invoking props.onClickTrigger(data) will execute the logic defined in this trigger.`,
    helpRenders,
  };
};

export default D3ChartManager;
